import { useState, useRef, useEffect } from 'react';
import { categories } from '@/mocks/categories';

export default function Hero() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCategorySelect = (cat: typeof categories[0]) => {
    setSelectedCategory(cat.name);
    setDropdownOpen(false);
    window.open(cat.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <section className="relative w-full min-h-screen flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://readdy.ai/api/search-image?query=Soft%20warm%20gradient%20background%20with%20organic%20abstract%20shapes%20in%20muted%20sage%20green%20and%20terracotta%20tones%2C%20gentle%20flowing%20curves%20suggesting%20modern%20retail%20elegance%2C%20subtle%20textile%20texture%20overlay%2C%20diffused%20golden%20hour%20lighting%20from%20upper%20right%20corner%2C%20minimalist%20artistic%20composition%20with%20negative%20space%20in%20center%20for%20text%20overlay%2C%20calm%20sophisticated%20atmosphere%2C%20contemporary%20editorial%20background%2C%20no%20human%20figures%2C%20abstract%20warm%20geometric%20elements%20blending%20softly&width=1920&height=1080&seq=superbuy-hero-bg-v2&orientation=landscape"
          alt="SuperBuy Spreadsheet Product Discovery"
          className="w-full h-full object-cover object-top"
          loading="eager"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-white/50 via-white/25 to-white/60"></div>
      </div>

      <div className="relative z-10 w-full max-w-4xl mx-auto px-6 text-center pt-20 pb-10">
        <p className="font-label text-xs md:text-sm text-primary-600 tracking-[0.2em] uppercase mb-4 animate-fade-in opacity-0 [animation-fill-mode:forwards]">
          Independent Shopping Research Platform
        </p>

        <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-foreground-950 leading-tight mb-4 animate-fade-in-up opacity-0 [animation-fill-mode:forwards]">
          SuperBuy Spreadsheet
          <br />
          <span className="font-sans text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light text-foreground-600">
            Smart Shopping Guide
          </span>
        </h1>

        <p className="font-body text-sm md:text-base text-foreground-500 font-light max-w-2xl mx-auto mb-8 animate-fade-in-up animate-delay-100 opacity-0 [animation-fill-mode:forwards]">
          Find organized SuperBuy spreadsheets, product collections, buying guides and research
          tools designed to help shoppers discover products faster and make smarter purchasing decisions.
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10 animate-fade-in-up animate-delay-200 opacity-0 [animation-fill-mode:forwards]">
          <div ref={dropdownRef} className="relative w-full sm:w-72">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="w-full flex items-center justify-between bg-white/90 backdrop-blur-sm border border-background-300 rounded-full px-5 py-3.5 text-sm text-foreground-700 font-body cursor-pointer hover:border-foreground-300 transition-all duration-200"
            >
              <span className={selectedCategory ? 'text-foreground-900' : 'text-foreground-400'}>
                {selectedCategory || 'Select a category...'}
              </span>
              <i className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full mt-2 left-0 right-0 bg-white rounded-xl border border-background-200 shadow-lg overflow-hidden z-20 max-h-64 overflow-y-auto animate-scale-in">
                {categories.map((cat) => (
                  <button
                    key={cat.name}
                    onClick={() => handleCategorySelect(cat)}
                    className="w-full text-left px-5 py-3 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors duration-150 font-body cursor-pointer whitespace-nowrap border-b border-background-100 last:border-b-0"
                  >
                    {cat.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="btn-primary w-full sm:w-auto text-center"
          >
            Explore SuperBuy Sheet
            <i className="ri-arrow-right-line ml-2"></i>
          </a>
        </div>

        <p className="font-body text-xs text-foreground-400 animate-fade-in animate-delay-300 opacity-0 [animation-fill-mode:forwards]">
          Independent resource platform helping global shoppers research products efficiently
        </p>
      </div>

      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 animate-fade-in animate-delay-500 opacity-0 [animation-fill-mode:forwards]">
        <button
          onClick={() => document.getElementById('categories')?.scrollIntoView({ behavior: 'smooth' })}
          className="flex flex-col items-center gap-2 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer"
        >
          <span className="font-label text-xs tracking-widest uppercase">Discover</span>
          <i className="ri-arrow-down-line text-lg animate-bounce"></i>
        </button>
      </div>
    </section>
  );
}